import moment from 'moment-timezone'

import { camelize } from 'services/string_utils'
import { formatTime } from 'services/time_format'

import { TYPE_INTERNAL, TYPE_EXTERNAL } from './constants'
import { memberEventsTree } from './member_event_tree_utils'

const isCalendlyEvent = ({ kind }) => ['calendly', 'buffer'].includes(kind)
const isInternalEvent = ({ kind }) => ['calendly', 'reserved', 'buffer'].includes(kind)

const isSecondaryOrCalendly = ({ hostType, kind }) => hostType === 'secondary' || isInternalEvent({ kind })
const isSecondaryOrReserved = ({ hostType, kind, isDoubleBookable }) => (
  (hostType === 'secondary' && !isDoubleBookable) || kind === 'reserved'
)

const isHoliday = (event) => event.kind === 'holiday'

const isSecondaryOrCalendlyButNotHoliday = (event) => isSecondaryOrCalendly(event) && !isHoliday(event)
const isSecondaryOrReservedButNotHoliday = (event) => isSecondaryOrReserved(event) && !isHoliday(event)

const sortByStartTime = (a, b) => (moment(a.start) < moment(b.start) ? -1 : 1)

const timeRange = ({ start, end, timezone, timeNotation }) => {
  const fromStr = formatTime(moment(start).tz(timezone), timeNotation)
  const toStr = formatTime(moment(end).tz(timezone), timeNotation)

  return `${fromStr} - ${toStr}`
}

const hasOverlap = (start, end, spotStart, spotEnd) => (
  !(start.isSameOrAfter(spotEnd) || end.isSameOrBefore(spotStart))
)

const overlapsSelectedSpot = (selectedSpots, spotStart, spotEnd) => (
  selectedSpots.some(({ start, end }) => hasOverlap(start, end, spotStart, spotEnd))
)

const overlapsLastAddedSpot = (lastAddedSpot, currentSpotStart) => {
  if (!lastAddedSpot) return false

  return !currentSpotStart.isSameOrAfter(lastAddedSpot.end)
}

const calculateNewSpots = (freeSpots, selectedSpots, duration) => {
  if (freeSpots.length === 0) return []

  return freeSpots.reduce((acc, currentSpot) => {
    const lastAddedSpot = acc[acc.length - 1]
    const currentSpotStart = currentSpot.start
    const currentSpotEnd = currentSpotStart.clone().add(duration, 'minutes')

    const currentSpotOverlapsLastAddedSpot = overlapsLastAddedSpot(lastAddedSpot, currentSpotStart)
    const currentSpotOverlapsSelectedSpot = overlapsSelectedSpot(selectedSpots, currentSpotStart, currentSpotEnd)

    if (!currentSpotOverlapsLastAddedSpot && !currentSpotOverlapsSelectedSpot) {
      acc.push(currentSpot)
    }

    return acc
  }, [])
}

const buildSeparateBufferedEvents = (eventData, isOwnerEvent) => {
  const events = []

  if (!eventData) return events
  if (!isOwnerEvent) return eventData

  eventData.forEach((event) => {
    const {
      startTime,
      endTime,
      bufferedStartTime,
      bufferedEndTime,
      beforeBufferName,
      afterBufferName
    } = camelize(event)

    if (bufferedStartTime < startTime) {
      events.push({
        start_time: bufferedStartTime,
        end_time: startTime,
        name: beforeBufferName,
        event_id: event.event_id,
        color: event.color,
        kind: 'buffer'
      })
    }

    events.push(event)

    if (bufferedEndTime > endTime) {
      events.push({
        start_time: endTime,
        end_time: bufferedEndTime,
        name: afterBufferName,
        event_id: event.event_id,
        color: event.color,
        kind: 'buffer'
      })
    }
  })

  return events
}

const formatEventData = (eventData, {
  isOwnerEvent = false,
  hostIndex = 0,
  colorIndex = undefined,
  userInitials = ''
} = {}) => buildSeparateBufferedEvents(eventData, isOwnerEvent).map(({
  buffered_start_time: bufferedStartTime,
  buffered_end_time: bufferedEndTime,
  start_time: start,
  end_time: end,
  name: title,
  event_id: eventId,
  pull_calendar_id: pullCalendarId,
  calendar_account_uuid: calendarAccountUuid,
  color,
  kind,
  all_day: allDay,
  double_bookable: isDoubleBookable
}) => ({
  title: isOwnerEvent ? title : '',
  hostType: isOwnerEvent ? 'primary' : 'secondary',
  isCalendlyEvent: isInternalEvent({ kind }),
  start: isOwnerEvent ? start : bufferedStartTime,
  end: isOwnerEvent ? end : bufferedEndTime,
  isDoubleBookable,
  eventId,
  hostIndex,
  colorIndex,
  color,
  kind,
  userInitials,
  pullCalendarId,
  calendarAccountUuid,
  allDay
}))

const formatHolidayData = (holidayData, {
  isOwnerEvent = false,
  hostIndex = 0,
  colorIndex = undefined,
  userInitials = ''
} = {}) => holidayData?.filter((holiday) => holiday.enabled).map(({
  date_observed: dateObserved,
  name: title,
  key: eventId
}) => ({
  title,
  hostType: isOwnerEvent ? 'primary' : 'secondary',
  isCalendlyEvent: false,
  start: moment.utc(dateObserved).format('YYYY-MM-DD'),
  end: moment.utc(dateObserved).add(1, 'day').format('YYYY-MM-DD'),
  eventId,
  hostIndex,
  colorIndex,
  kind: 'holiday',
  userInitials,
  allDay: true
}))

const obtainUserInitials = (name) => name.split(' ').map((namePart) => namePart.charAt(0).toUpperCase()).join('')

const getSelectedMemberEvents = (selectedMembers, currentUserId) => (
  selectedMembers.map(({ user_id: userId, name, eventData, holidays, colorIndex }, hostIndex) => {
    const userInitials = obtainUserInitials(name)
    const isOwnerEvent = currentUserId === userId

    const events = []
    if (eventData) {
      events.push(...formatEventData(eventData, {
        isOwnerEvent, hostIndex, colorIndex, userInitials
      }))
    }

    if (holidays) {
      events.push(...formatHolidayData(holidays, {
        isOwnerEvent, hostIndex, colorIndex, userInitials
      }))
    }

    return events
  }).flat().sort(sortByStartTime)
)

const getOverlappingEvents = (memberEvents, start, end) => memberEvents.search([start.unix() + 1, end.unix() - 1])

const getDoubleBookingEvents = (overlappingEvents, allowInternalEventsOverlap = false) => {
  const externalEvents = overlappingEvents
    .filter((event) => !isSecondaryOrCalendly(event) && !isHoliday(event))
    .map(({ eventId, pullCalendarId, calendarAccountUuid }) => (
      { type: TYPE_EXTERNAL, eventId, pullCalendarId, calendarAccountUuid }
    ))

  if (!allowInternalEventsOverlap) return externalEvents

  const internalEvents = overlappingEvents
    .filter((event) => isCalendlyEvent(event))
    .map(({ eventId }) => (
      { type: TYPE_INTERNAL, eventUuid: eventId }
    ))

  return [...internalEvents, ...externalEvents]
}

const shiftSpotsDuration = (spots, durationInMinutes) => [...spots].map((spot) => ({
  ...spot, end: spot.start.clone().add(durationInMinutes, 'minutes')
}))

const calculateDurationInMinutes = ({ customDurationValue, duration, customDurationUnit }) => {
  const durationValue = Number(customDurationValue || duration)
  const durationUnit = customDurationValue ? customDurationUnit : 'minutes'
  return moment.duration(durationValue, durationUnit).asMinutes()
}

const spotsWithConflicts = (selectedSpots, tree, allowInternalConflicts) => selectedSpots.filter(({ start, end }) => {
  const overlappingEvents = getOverlappingEvents(tree, start, end)
  const filter = allowInternalConflicts ? isSecondaryOrReserved : isSecondaryOrCalendly
  return overlappingEvents.filter(filter).length === 0
})

const spotsWithoutConflicts = (selectedSpots, tree) => selectedSpots.filter(({ start, end }) => {
  const overlappingEvents = getOverlappingEvents(tree, start, end)
  return overlappingEvents.length === 0
})

const updateDoubleBooking = (spots, tree, allowInternalConflicts) => spots.map((spot) => {
  const overlappingEvents = getOverlappingEvents(tree, spot.start, spot.end)
  spot.doubleBookingEvents = getDoubleBookingEvents(overlappingEvents, allowInternalConflicts)
  return spot
})

const removeConflictingSpots = (selectedSpots, groupTimezone, selectedMembers,
  { timezone, id: currentUserId }, allowExternalConflicts = true, allowInternalConflicts = false) => {
  const tz = groupTimezone || timezone
  const memberEvents = getSelectedMemberEvents(selectedMembers, currentUserId)
  const tree = memberEventsTree(memberEvents, tz)
  const spots = allowExternalConflicts
    ? spotsWithConflicts(selectedSpots, tree, allowInternalConflicts)
    : spotsWithoutConflicts(selectedSpots, tree)
  const spotsWithDoubleBooking = updateDoubleBooking(spots, tree, allowInternalConflicts)
  return spotsWithDoubleBooking
}

const maybeRemoveInvitees = (selectedSpots, eventTypeDuration, newDurationInMinutes) => {
  if (!Number.isInteger(eventTypeDuration) || !Number.isInteger(newDurationInMinutes)) return selectedSpots
  if (newDurationInMinutes <= eventTypeDuration) return selectedSpots

  return selectedSpots.map(({ invitees: _invitees, ...spot }) => ({ ...spot }))
}

export {
  timeRange,
  isSecondaryOrCalendlyButNotHoliday,
  isSecondaryOrReservedButNotHoliday,
  sortByStartTime,
  calculateNewSpots,
  calculateDurationInMinutes,
  shiftSpotsDuration,
  getSelectedMemberEvents,
  maybeRemoveInvitees,
  removeConflictingSpots,
  getOverlappingEvents,
  getDoubleBookingEvents
}
